import * as React from 'react';
import { NavigateFunction } from 'react-router-dom';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { EditType } from '../BookingDetailedView';
import {
	checkBookingCanBeModified,
	discardAlterations,
} from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { clearAllFerryBookingWizardDataFromStorage } from 'Util/_HumanWritten/BookingWizard/BookingWizardUtils';
import { setTransactionPriceInStorage } from 'Util/_HumanWritten/CartPriceSessionStorage';
import { redirectEditTo } from './RedirectEditTo';
import { BookingEntity } from 'Models/Entities';
import { isBeforeNow } from 'Util/_HumanWritten/GeneralHelpers/DateTimeHelpers';
import { ProcessResults } from './EditCancelPopUp';
import { discountEditPopUp } from './DiscountEditPopUp';
import { isNullOrUndefined } from 'Util/TypeGuards';

export const onEdit = async (
	navigate: NavigateFunction,
	setWizardData: (bookingWizardData: BookingWizardData) => void,
	setOldWizardData: (bookingWizardData: BookingWizardData) => void,
	booking: BookingEntity,
	editType: EditType,
	bookingIsDepartureOfTwoWayBooking: boolean,
	// Whether the booking wizard will render its mobile layout (see redirectEditTo).
	isMobileWizardView: boolean,
	// When supplied (from the guided edit flow, where the user has already chosen which trip(s)
	// to edit), the internal "One way / Both trips" confirm modal is skipped and this value is
	// used for `alterReturn` instead. Leave undefined to keep the original prompt behaviour.
	alterReturnOverride?: boolean,
) => {
	// We need to make sure there are no inprogress alterations before we continue to the booking wizard
	// or else pricing calculations will be off (as we need to use the most recent `RESERVED` alteration
	// in order to know what price changes have been made.
	await discardAlterations(navigate, {
		bookingIds: [booking.id],
		systemExpired: true,
	});
	const res = await checkBookingCanBeModified(booking.id);
	const canContinueWithCancel = await ProcessResults(res, false);
	if (!canContinueWithCancel) {
		return;
	}

	// Logic breakdown
	//
	//     if booking has associated return ticket, confirm changes apply to both or just one
	//     then if a discount is applied, notify the user of alteration restrictions
	//     finally navigate user to specified booking wizard step
	//
	//     Note: the cut-off fee confirmation modal was removed — the applicable fee is now
	//     surfaced directly in the edit booking wizard.
	if (!booking.bookedSummary) {
		console.error('Booked summary missing, this should not happen');
		return;
	}

	// Confirm changes apply to both or just one booking

	let alterReturn = alterReturnOverride ?? false;

	// Only prompt for the one-way/both-trips choice when the caller hasn't already made it.
	if (alterReturnOverride === undefined && !booking.desynced && !res.includes('desynced')) {
		// When booking is synced, there is an associated booking
		const returnBookingInfo = booking?.returnBooking ?? booking?.returnBookingFor;
		const returnBookingClosed = returnBookingInfo?.bookedSummary?.ferryTrip.isClosedForSpecificBookingType(
			booking.typeOfBooking(),
		);

		if (returnBookingInfo && !returnBookingInfo.bookedSummary) {
			console.error('Booked summary missing, this should not happen');
			return;
		}

		if (
			returnBookingInfo &&
			returnBookingInfo.bookedSummary &&
			returnBookingInfo.bookingStatus === 'BOOKED' &&
			!returnBookingInfo.checkedIn &&
			isNullOrUndefined(returnBookingInfo.checkInStatusId) &&
			!returnBookingClosed &&
			!isBeforeNow(returnBookingInfo.bookedSummary.ferryTrip.departureDateTime)
		) {
			try {
				alterReturn = await newConfirmModal(
					'We noticed you have a returning trip...',
					<div>
						<h5>Do you wish to make changes to those trip details too?</h5>
					</div>,
					{
						cancelText: 'One way',
						confirmText: 'Both trips',
						resolveOnCancel: true,
						resolveOnClose: true,
						shouldCloseOnEsc: false,
						shouldCloseOnOverlayClick: false,
						showCloseButton: true,
					},
				);
			} catch {
				// Reaching here means the user closed the modal or dismissed it
				return;
			}
		}
	}

	let continueToEdit = true;

	const discounted = booking.bookedSummary.discountAmount > 0;

	// Discounted bookings restrict which fields can be altered, so still notify the user.
	if (discounted) {
		continueToEdit = await discountEditPopUp(discounted);
	}

	if (!continueToEdit) {
		return;
	}

	// Clear the local storage and reset total cart prices back to $0
	clearAllFerryBookingWizardDataFromStorage();
	setTransactionPriceInStorage(0);

	if (alterReturn && !bookingIsDepartureOfTwoWayBooking) {
		// If editing both trips using the edit button of the return leg of a booking,
		// switch fees around
		redirectEditTo(setWizardData, setOldWizardData, booking, isMobileWizardView, editType, alterReturn, navigate);
	} else {
		redirectEditTo(setWizardData, setOldWizardData, booking, isMobileWizardView, editType, alterReturn, navigate);
	}
};
